import React from "react";
import {
  ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, BarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine
} from "recharts";

/** Shared palette + core chart blocks for the Principal / Teacher / Student dashboards. */
export const COLORS = {
  present: "#10b981", // Emerald-500
  absent: "#f43f5e",  // Rose-500
  leave: "#f59e0b",   // Amber-500
  academic: "#6366f1", // Indigo-500
  sky: "#0ea5e9",      // Sky-500
  grid: "#e2e8f0",    // Slate-200
  text: "#64748b"     // Slate-500
};

export const axis = {
  stroke: "#64748b",
  fontSize: 12,
  fontWeight: 600,
  tickLine: false,
  axisLine: false,
  tick: { fill: "#334155", fontSize: 12, fontWeight: 600 }
};

export const legend = {
  wrapperStyle: {
    paddingTop: "15px",
    color: "#334155",
    fontSize: "12px",
    fontWeight: 600
  }
};

export const shortDate = (d) => (d ? String(d).slice(5) : "");

export const box = {
  backgroundColor: "#ffffff",
  borderColor: "#cbd5e1",
  borderRadius: "0.75rem",
  boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
  color: "#0f172a",
  fontSize: "13px",
  fontWeight: "600",
  padding: "10px 15px"
};

/** Custom tooltip: label + one coloured row per series. */
export function Tip({ active, payload, label, suffix = "", labelFormatter }) {
  if (!active || !payload || !payload.length) return null;
  const p0 = payload[0].payload || {};
  return (
    <div style={box}>
      <div style={{ color: "#64748b", marginBottom: 4, fontSize: "11px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {labelFormatter ? labelFormatter(label) : (p0.name || label)}
      </div>
      {payload.map((p) => (
        <div key={p.dataKey || p.name} style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 2 }}>
          <span style={{ width: 8, height: 8, borderRadius: "50%", background: p.color || p.fill }} />
          <span style={{ color: "#334155" }}>{p.name}: <span style={{ color: "#0f172a" }}>{p.value == null ? "-" : `${p.value}${suffix}`}</span></span>
        </div>
      ))}
    </div>
  );
}

export const Wrap = ({ children, height = 260 }) => (
  <ResponsiveContainer width="100%" height={height} minHeight={260}>{children}</ResponsiveContainer>
);

/** Donut: Present / Absent / Leave with a centre label. */
export function AttendancePie({ present = 0, absent = 0, leave = 0, centerLabel }) {
  const data = [
    { name: "Present", value: present, color: COLORS.present },
    { name: "Absent", value: absent, color: COLORS.absent },
    { name: "Leave", value: leave, color: COLORS.leave }
  ];
  return (
    <div className="relative">
      <Wrap>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" innerRadius={62} outerRadius={92} paddingAngle={3} stroke="none">
            {data.map((d) => <Cell key={d.name} fill={d.color} />)}
          </Pie>
          <Tooltip content={<Tip />} />
          <Legend {...legend} verticalAlign="bottom" />
        </PieChart>
      </Wrap>
      <div className="pointer-events-none absolute left-0 right-0 text-center" style={{ top: 90 }}>
        <div className="text-3xl font-black text-slate-900">{centerLabel != null ? centerLabel : present + absent + leave}</div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500">{centerLabel != null ? "attendance" : "total"}</div>
      </div>
    </div>
  );
}

/** Multi-line chart. series = [{ key, name, color }] */
export function MultiLine({ data, series, xKey = "date", xFormat = shortDate, domain = [0, 100], suffix = "%", refLine }) {
  return (
    <Wrap>
      <LineChart data={data} margin={{ top: 8, right: 12, left: -16, bottom: 0 }}>
        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={xKey} tickFormatter={xFormat} {...axis} />
        <YAxis domain={domain} unit={suffix} {...axis} />
        <Tooltip content={<Tip suffix={suffix} labelFormatter={xFormat} />} />
        <Legend {...legend} />
        {refLine != null && <ReferenceLine y={refLine} stroke={COLORS.leave} strokeDasharray="4 4" />}
        {series.map((s) => <Line key={s.key} type="monotone" dataKey={s.key} name={s.name} stroke={s.color} strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} connectNulls />)}
      </LineChart>
    </Wrap>
  );
}

/** Horizontal grouped bars (class comparison). */
export function HBars({ data, bars, labelKey = "label", height = 360 }) {
  return (
    <Wrap height={height}>
      <BarChart data={data} layout="vertical" margin={{ top: 4, right: 16, left: 8, bottom: 0 }} barCategoryGap="22%">
        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" horizontal={false} />
        <XAxis type="number" domain={[0, 100]} unit="%" {...axis} />
        <YAxis type="category" dataKey={labelKey} width={64} interval={0} {...axis} />
        <Tooltip content={<Tip suffix="%" />} cursor={{ fill: "rgba(100, 116, 139, 0.1)" }} />
        <Legend {...legend} />
        {bars.map((b) => <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[0, 6, 6, 0]} />)}
      </BarChart>
    </Wrap>
  );
}

/** Vertical grouped bars (subjects). */
export function VBars({ data, bars, xKey = "label", domain = [0, 100] }) {
  return (
    <Wrap>
      <BarChart data={data} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
        <CartesianGrid stroke={COLORS.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={xKey} {...axis} />
        <YAxis domain={domain} unit="%" {...axis} />
        <Tooltip content={<Tip suffix="%" />} cursor={{ fill: "rgba(100, 116, 139, 0.1)" }} />
        <Legend {...legend} />
        {bars.map((b) => <Bar key={b.key} dataKey={b.key} name={b.name} fill={b.color} radius={[6, 6, 0, 0]} />)}
      </BarChart>
    </Wrap>
  );
}
